import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, cn, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../shared/format";
import { ConversationAvatar } from "./conversation-avatar";
import { AssigneeBadge, ConversationStatusBadge } from "./conversation-badges";
import type { ConversationItem } from "./inbox-model";
import { LanguageChip } from "./language-chip";
import { PriorityChip, SlaChip } from "./priority-sla-chips";
import type { QueueOrderEntry } from "./queue-sort";
import { QueueWaitChip } from "./queue-wait-chip";
import { SnoozeChip } from "./snooze-chip";

interface ConversationListItemProps {
  item: ConversationItem;
  selected: boolean;
  now: number;
  /** Where the weighted queue puts this chat and how long it has waited (live chat ops, Needs staff view). */
  queue?: QueueOrderEntry;
  queueFetchedAt?: number;
}

/** One row of the conversation list; the whole row is the link that opens the thread. */
export function ConversationListItem({ item, selected, now, queue, queueFetchedAt }: ConversationListItemProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useFormatters();
  const muted = item.status === "closed" || item.status === "abandoned";
  const strong = item.unread && !selected;
  return (
    <li data-conversation-id={item.id}>
      <AppLink
        href={consoleRoutes.inbox(summary.slug, item.id)}
        aria-current={selected ? "page" : undefined}
        className={cn(
          "relative flex gap-3 px-4 py-3 outline-none hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
          selected && "bg-primary-soft hover:bg-primary-soft",
        )}
      >
        <ConversationAvatar name={item.visitor_name} channel={item.channel} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn("truncate text-sm", strong ? "font-extrabold" : "font-semibold", muted && "text-fg-muted")}>{item.visitor_name}</span>
            {item.verified ? (
              <Badge tone="brand" className="h-4 px-1.5 text-[10px]">
                <Icon name="shield-check" className="size-2.5" />
                {t("engage.inbox.list.verified")}
              </Badge>
            ) : null}
            <span className={cn("ml-auto shrink-0 text-[11px]", strong ? "font-semibold text-primary-ink" : "text-fg-subtle")}>{fmt.ago(item.last_message_at, now)}</span>
          </div>
          <p className={cn("truncate text-xs", strong ? "font-semibold text-fg" : muted ? "text-fg-subtle" : "text-fg-muted")}>{item.preview ?? item.title ?? ""}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {!item.needs_staff && item.has_lead ? (
              <Badge tone="brand">
                <Icon name="user-check" className="size-3" />
                {t("engage.inbox.status.lead")}
              </Badge>
            ) : (
              <ConversationStatusBadge item={item} />
            )}
            <AssigneeBadge item={item} />
            <SnoozeChip item={item} now={now} />
            {queue && queueFetchedAt !== undefined ? <QueueWaitChip entry={queue} fetchedAt={queueFetchedAt} now={now} /> : null}
            <PriorityChip priority={item.priority} />
            <LanguageChip code={item.detected_language} />
            <SlaChip target={item.sla_target} now={now} />
            {strong ? (
              <span className="ml-auto size-2 rounded-pill bg-primary">
                <span className="sr-only">{t("engage.inbox.list.unread")}</span>
              </span>
            ) : null}
          </div>
        </div>
      </AppLink>
    </li>
  );
}
